'use strict';

var EMBLA_BREAKPOINT = 992;
var resizeListenerBound = false;
var EmblaCarouselConstructor = null;
var lastViewportMode = null;

/**
 * Read a slick option for the current viewport, honoring responsive breakpoints.
 * @param {Object} config - slick configuration
 * @param {string} optionName - option key
 * @returns {*} option value
 */
function getSlickOption(config, optionName) {
    var value = config[optionName];

    if (!config.responsive || !config.responsive.length) {
        return value;
    }

    if (config.mobileFirst) {
        config.responsive
            .slice()
            .sort(function (a, b) { return a.breakpoint - b.breakpoint; })
            .forEach(function (breakpoint) {
                if (window.innerWidth > breakpoint.breakpoint
                    && breakpoint.settings
                    && breakpoint.settings !== 'unslick'
                    && breakpoint.settings[optionName] !== undefined) {
                    value = breakpoint.settings[optionName];
                }
            });
    } else {
        config.responsive
            .slice()
            .sort(function (a, b) { return b.breakpoint - a.breakpoint; })
            .forEach(function (breakpoint) {
                if (window.innerWidth <= breakpoint.breakpoint
                    && breakpoint.settings
                    && breakpoint.settings !== 'unslick'
                    && breakpoint.settings[optionName] !== undefined) {
                    value = breakpoint.settings[optionName];
                }
            });
    }

    return value;
}

/**
 * Update prev/next button enabled state.
 * @param {Object} emblaApi - Embla carousel instance
 * @param {HTMLElement} rootNode - carousel root element
 */
function updateEmblaNavigationState(emblaApi, rootNode) {
    var prevBtn = rootNode.querySelector('.embla-button-prev');
    var nextBtn = rootNode.querySelector('.embla-button-next');

    if (!prevBtn || !nextBtn) {
        return;
    }

    if (emblaApi.canScrollPrev()) {
        prevBtn.classList.remove('slick-disabled');
        prevBtn.removeAttribute('disabled');
    } else {
        prevBtn.classList.add('slick-disabled');
        prevBtn.setAttribute('disabled', 'disabled');
    }

    if (emblaApi.canScrollNext()) {
        nextBtn.classList.remove('slick-disabled');
        nextBtn.removeAttribute('disabled');
    } else {
        nextBtn.classList.add('slick-disabled');
        nextBtn.setAttribute('disabled', 'disabled');
    }
}

/**
 * Setup navigation buttons for an Embla instance.
 * @param {Object} emblaApi - Embla carousel instance
 * @param {HTMLElement} rootNode - carousel root element
 */
function setupEmblaNavigation(emblaApi, rootNode) {
    var prevBtn = rootNode.querySelector('.embla-button-prev');
    var nextBtn = rootNode.querySelector('.embla-button-next');

    if (prevBtn) {
        prevBtn.addEventListener('click', emblaApi.scrollPrev, false);
    }
    if (nextBtn) {
        nextBtn.addEventListener('click', emblaApi.scrollNext, false);
    }

    emblaApi.on('select', function () { updateEmblaNavigationState(emblaApi, rootNode); });
    emblaApi.on('reInit', function () { updateEmblaNavigationState(emblaApi, rootNode); });
    emblaApi.on('init', function () { updateEmblaNavigationState(emblaApi, rootNode); });
    updateEmblaNavigationState(emblaApi, rootNode);
}

/**
 * Build an accessible pagination tab label (e.g. "1 of 2").
 * @param {number} current - one-based slide index
 * @param {number} total - total slide count
 * @returns {string} aria-label text
 */
function getPaginationTabLabel(current, total) {
    var carouselResources = window.carouselResources || {};
    var label = carouselResources.paginationTab || '%current% of %total%';

    return label.replace('%current%', current).replace('%total%', total);
}

/**
 * Setup pagination dots for an Embla instance.
 * @param {Object} emblaApi - Embla carousel instance
 * @param {HTMLElement} rootNode - carousel root element
 */
function setupEmblaPagination(emblaApi, rootNode) {
    var paginationEl = rootNode.querySelector('.embla-pagination');
    if (!paginationEl) {
        return;
    }

    if (!paginationEl.getAttribute('role')) {
        paginationEl.setAttribute('role', 'tablist');
    }

    var updateDots = function () {
        var dots = paginationEl.querySelectorAll('.embla-pagination-bullet');
        var selected = emblaApi.selectedScrollSnap();
        dots.forEach(function (dot, index) {
            var isSelected = index === selected;
            dot.classList.toggle('embla-pagination-bullet-active', isSelected);
            dot.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        });
    };

    var renderDots = function () {
        paginationEl.innerHTML = '';
        var snaps = emblaApi.scrollSnapList();
        var total = snaps.length;

        snaps.forEach(function (_, index) {
            var dot = document.createElement('button');
            dot.classList.add('embla-pagination-bullet');
            dot.type = 'button';
            dot.setAttribute('role', 'tab');
            dot.setAttribute('aria-selected', 'false');
            dot.setAttribute('aria-label', getPaginationTabLabel(index + 1, total));
            dot.addEventListener('click', function () { emblaApi.scrollTo(index); });
            paginationEl.appendChild(dot);
        });
        updateDots();
    };

    renderDots();
    emblaApi.on('select', updateDots);
    emblaApi.on('reInit', renderDots);
}

/**
 * Update aria-hidden on slides based on which are currently in view.
 * @param {Object} emblaApi - Embla carousel instance
 * @param {HTMLElement} rootNode - carousel root element
 */
function updateSlideAriaVisibility(emblaApi, rootNode) {
    var slides = rootNode.querySelectorAll('.embla-slide');
    var inViewIndexes = emblaApi.slidesInView();
    slides.forEach(function (slide, index) {
        var isHidden = inViewIndexes.indexOf(index) === -1;
        slide.setAttribute('aria-hidden', isHidden ? 'true' : 'false');
        var focusables = slide.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        focusables.forEach(function (el) {
            if (isHidden) {
                if (!el.hasAttribute('data-original-tabindex')) {
                    var originalTabindex = el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : null;
                    el.setAttribute('data-original-tabindex', originalTabindex === null ? '' : originalTabindex);
                }
                el.setAttribute('tabindex', '-1');
            } else {
                var savedTabindex = el.getAttribute('data-original-tabindex');
                if (savedTabindex !== null) {
                    if (savedTabindex === '') {
                        el.removeAttribute('tabindex');
                    } else {
                        el.setAttribute('tabindex', savedTabindex);
                    }
                    el.removeAttribute('data-original-tabindex');
                }
            }
        });
    });
}

/**
 * Destroy an Embla instance and hide carousel controls.
 * @param {HTMLElement} rootNode - carousel root element
 */
function destroyEmblaCarousel(rootNode) {
    if (rootNode.emblaApiRef) {
        rootNode.emblaApiRef.destroy();
        rootNode.emblaApiRef = null;
        rootNode.emblaScrollRef = null;
    }

    rootNode.classList.remove('embla-initialized');

    var paginationEl = rootNode.querySelector('.embla-pagination');
    if (paginationEl) {
        paginationEl.innerHTML = '';
    }

    var prevBtn = rootNode.querySelector('.embla-button-prev');
    var nextBtn = rootNode.querySelector('.embla-button-next');
    if (prevBtn) {
        prevBtn.classList.add('slick-disabled');
        prevBtn.setAttribute('disabled', 'disabled');
    }
    if (nextBtn) {
        nextBtn.classList.add('slick-disabled');
        nextBtn.setAttribute('disabled', 'disabled');
    }
}

/**
 * Create an Embla instance on a carousel root element.
 * @param {HTMLElement} rootNode - carousel root element
 * @param {Function} EmblaCarousel - Embla constructor
 * @param {number} slidesToScroll - slides to scroll per navigation action
 * @returns {Object|null} Embla API instance
 */
function createEmblaCarousel(rootNode, EmblaCarousel, slidesToScroll) {
    var viewportNode = rootNode.querySelector('.embla__viewport');
    if (!viewportNode) {
        return null;
    }

    var emblaApi = EmblaCarousel(viewportNode, { // eslint-disable-line new-cap
        loop: true,
        align: 'start',
        slidesToScroll: slidesToScroll
    });

    setupEmblaNavigation(emblaApi, rootNode);
    setupEmblaPagination(emblaApi, rootNode);

    updateSlideAriaVisibility(emblaApi, rootNode);
    emblaApi.on('select', function () { updateSlideAriaVisibility(emblaApi, rootNode); });
    emblaApi.on('reInit', function () { updateSlideAriaVisibility(emblaApi, rootNode); });

    rootNode.emblaApiRef = emblaApi;
    rootNode.emblaScrollRef = slidesToScroll;
    rootNode.classList.add('embla-initialized');

    return emblaApi;
}

/**
 * Refresh controls and Embla state for a responsive carousel.
 * Slide widths are defined by --slide-size in embla/_emblaOverwrites.scss.
 * @param {HTMLElement} rootNode - carousel root element
 * @param {Function} EmblaCarousel - Embla constructor
 */
function refreshResponsiveEmblaCarousel(rootNode, EmblaCarousel) {
    if (!rootNode.emblaConfigRef || !rootNode.emblaConfigRef.getSlidesConfig) {
        return;
    }

    var slideCount = rootNode.querySelectorAll('.embla-slide').length;
    if (slideCount <= 1) {
        destroyEmblaCarousel(rootNode);
        return;
    }

    var slidesConfig = rootNode.emblaConfigRef.getSlidesConfig();
    var slidesToShow = slidesConfig.slidesToShow;
    var slidesToScroll = slidesConfig.slidesToScroll || 1;

    if (slideCount <= slidesToShow) {
        destroyEmblaCarousel(rootNode);
        return;
    }

    var existingApi = rootNode.emblaApiRef;
    if (existingApi && rootNode.emblaScrollRef === slidesToScroll) {
        existingApi.reInit();
        rootNode.classList.add('embla-initialized');
        updateEmblaNavigationState(existingApi, rootNode);
        return;
    }

    destroyEmblaCarousel(rootNode);

    try {
        createEmblaCarousel(rootNode, EmblaCarousel, slidesToScroll);
    } catch (e) {
        console.error('Embla initialization failed:', e); // eslint-disable-line no-console
    }
}

/**
 * @returns {string} current viewport mode for Embla responsive configs
 */
function getViewportMode() {
    return window.innerWidth < EMBLA_BREAKPOINT ? 'mobile' : 'desktop';
}

/**
 * Refresh all responsive Embla carousels when the viewport mode changes.
 * @param {Function} EmblaCarousel - Embla constructor
 */
function refreshAllResponsiveEmblaCarousels(EmblaCarousel) {
    document.querySelectorAll('.embla-slider[data-embla-responsive="true"]').forEach(function (rootNode) {
        refreshResponsiveEmblaCarousel(rootNode, EmblaCarousel);
    });
}

/**
 * Bind a single resize listener for all responsive Embla carousels.
 * @param {Function} EmblaCarousel - Embla constructor
 */
function ensureEmblaResizeListener(EmblaCarousel) {
    EmblaCarouselConstructor = EmblaCarousel;
    lastViewportMode = getViewportMode();

    if (resizeListenerBound) {
        return;
    }

    resizeListenerBound = true;

    window.addEventListener('resize', function () {
        if (!EmblaCarouselConstructor) {
            return;
        }

        var currentMode = getViewportMode();
        if (currentMode === lastViewportMode) {
            return;
        }

        lastViewportMode = currentMode;
        refreshAllResponsiveEmblaCarousels(EmblaCarouselConstructor);
    });
}

/**
 * Initialize or refresh a responsive Embla carousel.
 * @param {HTMLElement} rootNode - carousel root element
 * @param {Function} EmblaCarousel - Embla constructor
 * @param {Function} getSlidesConfig - returns { slidesToShow, slidesToScroll }
 */
function initResponsiveEmblaCarousel(rootNode, EmblaCarousel, getSlidesConfig) {
    rootNode.emblaConfigRef = { getSlidesConfig: getSlidesConfig };

    if (!rootNode.getAttribute('data-embla-responsive')) {
        rootNode.setAttribute('data-embla-responsive', 'true');
        ensureEmblaResizeListener(EmblaCarousel);
    }

    refreshResponsiveEmblaCarousel(rootNode, EmblaCarousel);
}

module.exports = {
    EMBLA_BREAKPOINT: EMBLA_BREAKPOINT,
    getPaginationTabLabel: getPaginationTabLabel,
    getSlickOption: getSlickOption,
    setupEmblaNavigation: setupEmblaNavigation,
    setupEmblaPagination: setupEmblaPagination,
    updateSlideAriaVisibility: updateSlideAriaVisibility,
    destroyEmblaCarousel: destroyEmblaCarousel,
    refreshResponsiveEmblaCarousel: refreshResponsiveEmblaCarousel,
    initResponsiveEmblaCarousel: initResponsiveEmblaCarousel
};
